import { createFileRoute, Link } from "@tanstack/react-router";

import { services } from "@/lib/site";
import { IMAGES } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { Reveal } from "@/components/site/Reveal";
import { WaveDivider } from "@/components/site/WaveDivider";
import { SectionHeading } from "@/components/site/SectionHeading";
import { ServiceStack, CtaBand } from "@/components/site/CTA";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Cleaning Services in Wetumpka, AL | Pretty in Pink" },
      {
        name: "description",
        content:
          "Residential and commercial cleaning services in Wetumpka, AL — house cleaning, deep cleaning, move-in/move-out, office, and post-construction cleaning.",
      },
      { property: "og:title", content: "Cleaning Services | Wetumpka, AL" },
      {
        property: "og:description",
        content:
          "Residential and commercial cleaning services in Wetumpka, AL and surrounding areas.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
  }),
  component: ServicesIndex,
});

function ServicesIndex() {
  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ])}
      />
      <InnerHero
        eyebrow="Our Services"
        title="Cleaning services for homes & businesses"
        subtitle="Residential and commercial cleaning across Wetumpka and surrounding areas — pick a service to see what's included, who it's for, and how it works."
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
            ]}
          />
        </div>
      </InnerHero>

      <section className="px-4 py-14 md:px-6 md:py-20">
        <Reveal>
          <SectionHeading
            eyebrow="What We Do"
            title="Five ways we get your space pretty clean"
            intro="From weekly home upkeep to a full post-construction reset, here's everything we do."
          />
        </Reveal>
        <div className="mx-auto mt-12 max-w-5xl">
          <ServiceStack
            items={services.map((s) => ({
              slug: s.slug,
              name: s.name,
              short: s.short,
              blurb: s.blurb,
            }))}
          />
        </div>

        <Reveal className="mx-auto mt-16 max-w-3xl rounded-2xl border border-border bg-secondary/50 p-6 text-center md:p-8">
          <p className="text-lg text-ink text-pretty">
            Not sure which service fits?{" "}
            <span className="text-muted-foreground">
              Tell us about your space and what you'd like cleaned — we'll point you to the right
              option and put together a quote.
            </span>
          </p>
          <div className="mt-5">
            <Link
              to="/contact"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Contact us to figure it out →
            </Link>
          </div>
        </Reveal>
      </section>

      <WaveDivider variant="toBlush" />
      <CtaBand
        title="Ready for a pretty clean?"
        subtitle="Tell us about your home or business and we'll put together a quote — no obligation."
      />
    </>
  );
}
